import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Menu, X, ShoppingCart, User, LogOut, BookOpen, Calendar, Radio, GraduationCap, MessageCircle, Store, Users, MapPin, Settings } from 'lucide-react';
import { useState } from 'react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: '/teachers', label: 'Teachers', icon: Users },
    { to: '/katha', label: 'Katha', icon: BookOpen },
    { to: '/shop', label: 'Shop', icon: Store },
    { to: '/events', label: 'Events', icon: Calendar },
    { to: '/live-kirtan', label: 'Live Kirtan', icon: Radio },
    { to: '/courses', label: 'School', icon: GraduationCap },
    { to: '/directory', label: 'Directory', icon: MapPin },
    { to: '/chatbot', label: 'Ask AI', icon: MessageCircle },
  ];

  return (
    <nav className="nav-glass sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-10 h-10 bg-gradient-to-br from-saffron-500 to-gold-500 rounded-full flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">ੴ</span>
            </div>
            <span className="font-heading font-bold text-xl text-royal-600 hidden sm:block">Khalsa Sidak</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-saffron-600 transition-colors rounded-lg hover:bg-saffron-50"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Admin Button - only visible for admin user */}
            {user?.email?.toLowerCase() === 'khalsasidak13@gmail.com' && (
              <Link 
                to="/admin" 
                className="hidden sm:flex items-center gap-1 px-3 py-2 bg-royal-600 text-white text-sm font-medium rounded-lg hover:bg-royal-700 transition-colors"
                data-testid="nav-admin"
              >
                <Settings className="w-4 h-4" />
                Admin
              </Link>
            )}

            {user && (
              <Link to="/cart" className="relative p-2 hover:bg-saffron-50 rounded-full transition-colors" data-testid="nav-cart">
                <ShoppingCart className="w-5 h-5 text-gray-700" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-saffron-500 text-white text-xs rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 bg-royal-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">{user.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="menu-orders">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    My Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  {user?.email?.toLowerCase() === 'khalsasidak13@gmail.com' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-sm"
                  data-testid="nav-login"
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="btn-primary text-sm py-2"
                  data-testid="nav-register"
                >
                  Join Us
                </Button>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-saffron-50 hover:text-saffron-600 rounded-lg transition-colors"
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              {/* Admin link for mobile */}
              {user?.email?.toLowerCase() === 'khalsasidak13@gmail.com' && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 bg-royal-100 text-royal-700 hover:bg-royal-200 rounded-lg transition-colors font-medium"
                >
                  <Settings className="w-5 h-5" />
                  Admin Panel
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
